/**
 * BecomeOperator — public self-serve signup for prospective taxi operators.
 *
 * Submits an application (POST /api/operators/apply) that lands in the platform
 * owner's console for review. Hybrid onboarding: apply → approval → activation.
 * No tenant is provisioned here.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Building2, CheckCircle2, Loader2 } from "lucide-react";
import { AiAssist } from "../components/AiAssist";

const FIELDS = [
  { k: "company_name", label: "Company name *", ph: "Acme Cabs Ltd", req: true },
  { k: "contact_name", label: "Your name *", ph: "Jane Doe", req: true },
  { k: "contact_email", label: "Email *", ph: "you@acmecabs.com", type: "email", req: true },
  { k: "contact_phone", label: "Phone", ph: "+44 7…" },
  { k: "region", label: "Operating region", ph: "Basingstoke, Hampshire" },
  { k: "fleet_size", label: "Fleet size", ph: "e.g. 10-20 vehicles" },
];

export default function BecomeOperator() {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company_name || !form.contact_name || !form.contact_email) {
      setError("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/operators/apply", form);
      setDone(r.data);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold tracking-tight">
            <Building2 className="w-5 h-5 text-[#4C9AFF]" /> Dispatch OS
          </Link>
          <Link to="/login" className="text-sm text-slate-300 hover:text-white">Sign in</Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10">
        {done ? (
          <div data-testid="apply-success" className="rounded-2xl border border-emerald-200 bg-white p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900">Application received</h1>
            <p className="text-slate-600 mt-2">{done.message}</p>
            {done.reference && <p className="mt-3 text-sm text-slate-500">Reference: <span className="font-mono font-semibold text-slate-800">{done.reference}</span></p>}
            <Link to="/" className="inline-block mt-6 px-5 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-medium hover:bg-[#00238a]">Back to home</Link>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-slate-900">Run your taxi firm on Dispatch OS</h1>
              <p className="text-slate-600 mt-2 max-w-xl">Apply to operate on our multi-tenant dispatch platform — real-time allocation, an AI booking line, driver &amp; passenger apps and analytics. We review every application before activation.</p>
            </div>
            <form onSubmit={submit} data-testid="become-operator-form" className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
              <AiAssist
                formTitle="Become an operator — application"
                endpoint="/public/ai/form-fill"
                testId="operator-ai-assist"
                values={form}
                onFill={(v) => setForm((s) => ({ ...s, ...v }))}
                schema={[
                  ...FIELDS.map((f) => ({
                    name: f.k,
                    label: f.label.replace(" *", ""),
                    type: f.type || "text",
                    required: !!f.req,
                  })),
                  { name: "message", label: "Anything else about your operation", type: "textarea" },
                ]}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {FIELDS.map((f) => (
                  <div key={f.k} className={f.k === "company_name" ? "md:col-span-2" : ""}>
                    <label className="text-xs font-semibold text-slate-600">{f.label}</label>
                    <input
                      data-testid={`apply-${f.k}`}
                      type={f.type || "text"}
                      value={form[f.k] || ""}
                      onChange={(e) => set(f.k, e.target.value)}
                      placeholder={f.ph}
                      className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
                    />
                  </div>
                ))}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Anything else?</label>
                <textarea
                  data-testid="apply-message"
                  value={form.message || ""}
                  onChange={(e) => set("message", e.target.value)}
                  rows={3}
                  placeholder="Tell us about your operation…"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20"
                />
              </div>
              {error && <div data-testid="apply-error" className="text-sm text-red-600">{error}</div>}
              <button
                data-testid="apply-submit"
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#002FA7] text-white font-medium hover:bg-[#00238a] disabled:opacity-50"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit application"}
              </button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
